import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Home as HomeIcon,
  User as UserIcon,
  Brain as BrainIcon,
  ClipboardList as ClipboardListIcon,
  Calendar as CalendarIcon,
  CalendarDays as CalendarDaysIcon,
  BookOpen as BookOpenIcon,
  Menu as MenuIcon,
  X as XIcon,
  LogOut as LogOutIcon,
  Target,
  CheckSquare,
  FileText,
  Dumbbell,
  UserCircle,
  Image,
  Volume2,
  MessageCircle,
  MessageSquare,
  Focus as FocusIcon,
  Plus,
  Minus,
  ChevronRight,
  ChevronDown,
  Settings,
  Smartphone,
  Download
} from 'lucide-react';
import axios from 'axios'; // Import axios
import { useUser } from '../context/UserContext';
import API_URL from '../config';
import Modal from './Modal';
import VisionBoard from './VisionBoard';
import AudioVisualization from './AudioVisualization';
import AskYourCoach from './AskYourCoach';
import ContactUs from './ContactUs';
import MentalImageryModal from './MentalImageryModal';
import HomeScreenInstructions from './HomeScreenInstructions';
import logger from '../utils/logger';


const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useUser();
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    desktop: {},
    mobile: {
      'AI Tools': true
    }
  });

  // Check if user is admin
  const isAdmin = user && user.email === 'coach@mentaledgecoach.net';

  // Check if user is a coach (has teams)
  const isCoach = user && user.role === 'coach';

  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      setIsMobile(mobileCheck);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Check for modal parameter in URL on component mount and when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const modalParam = searchParams.get('modal');
    
    if (modalParam) {
      setActiveModal(modalParam);
    }
  }, [location]);

  const handleLogout = async () => {
    logger.debug('🚪 Navigation: Logout initiated');
    try {
      // First, try to terminate the backend session
      const token = localStorage.getItem('token');
      if (token) {
        logger.debug('🚪 Navigation: Attempting to terminate backend session');
        try {
          // Use axios instead of fetch for better error handling
          const response = await axios.post(`${API_URL}/api/users/logout`, {}, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000 // Increased timeout for more reliability
          });
          
          if (response.status === 200) {
            logger.debug('🚪 Navigation: Backend session terminated successfully');
          } else {
            logger.warn('🚪 Navigation: Backend session termination returned status:', response.status);
          }
        } catch (error) {
          logger.error('🚪 Navigation: Failed to terminate backend session:', error);
          // Continue with logout even if backend call fails
        }
      }
      
      // Clear localStorage tokens directly to ensure they're removed
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      // Then call the UserContext logout function
      logger.debug('🚪 Navigation: Calling UserContext logout function');
      await logout();
      logger.debug('🚪 Navigation: UserContext logout completed');
      
      // Finally navigate to login page
      logger.debug('🚪 Navigation: Navigating to login page');
      navigate('/login');
    } catch (error) {
      logger.error('🚪 Navigation: Error during logout process:', error);
      
      // Ensure tokens are cleared even if there's an error
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      
      // Ensure we still navigate to login even if there's an error
      navigate('/login');
    }
  };

  const toggleSection = (section, view = 'mobile') => {
    setExpandedSections(prev => ({
      ...prev,
      [view]: {
        ...prev[view],
        [section]: !prev[view][section]
      }
    }));
  };

  // Main navigation items
  const navItems = [
    { 
      path: isCoach ? '/coach-dashboard' : '/dashboard', 
      label: 'Dashboard', 
      icon: HomeIcon 
    },
    { path: '/mvp', label: 'MVP', icon: CalendarIcon },
    { path: '/mindset', label: 'Mindset', icon: BrainIcon },
    { path: '/daily-success-checklist', label: 'Habits', icon: CheckSquare },
    { path: '/focus', label: 'Focus', icon: FocusIcon },
    { path: '/journal', label: 'Journal', icon: BookOpenIcon },
  ];

  // Bottom mobile menu items (visible without hamburger)
  const mobilePrimaryItems = [
    { 
      path: isCoach ? '/coach-dashboard' : '/dashboard', 
      label: 'Dashboard', 
      icon: HomeIcon 
    },
    { path: '/mvp', label: 'MVP', icon: CalendarIcon },
    { path: '/mindset', label: 'Mindset', icon: BrainIcon },
    { path: '/daily-success-checklist', label: 'Habits', icon: CheckSquare },
    { path: '/focus', label: 'Focus', icon: FocusIcon },
    { path: '/journal', label: 'Journal', icon: BookOpenIcon },
  ];

  // Hamburger menu items
  const hamburgerItems = [
    {
      label: 'AI Tools',
      icon: BrainIcon,
      subItems: [
        {
          id: 'audio-visualization',
          label: 'Mindset Audio',
          icon: Volume2,
          path: '/audio-visualization'
        },
        {
          id: 'mental-imagery',
          label: 'Mental Imagery',
          icon: Image,
          path: '/mental-imagery'
        },
        {
          id: 'ask-your-coach',
          label: 'Ask Your Coach',
          icon: MessageCircle,
          component: AskYourCoach
        }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  // Handle exercise click
  const handleExerciseClick = (exercise) => {
    setActiveModal(exercise);
    navigate(`${location.pathname}?modal=${exercise}`);
  };

  // Render a section with expandable functionality
  const renderDesktopSection = (item) => {
    const isExpanded = expandedSections.desktop[item.label] || false;
    
    return (
      <div key={item.label} className="relative group">
        <button
          onClick={() => toggleSection(item.label, 'desktop')}
          className="flex items-center px-4 py-2 text-gray-300 rounded hover:bg-gray-700 hover:text-white"
        >
          <item.icon className="mr-3 h-5 w-5" />
          <span>{item.label}</span>
          {isExpanded ? (
            <Minus className="ml-2 h-4 w-4" />
          ) : (
            <Plus className="ml-2 h-4 w-4" />
          )}
        </button>
        
        {isExpanded && (
          <div className="mt-2 pl-4 space-y-2">
            {item.subItems.map((subItem) => (
              subItem.path ? (
                <Link
                  key={subItem.path}
                  to={subItem.path}
                  className={`flex items-center px-4 py-2 text-sm text-gray-300 rounded hover:bg-gray-700 hover:text-white ${
                    isActive(subItem.path) ? 'bg-gray-700 text-white' : ''
                  }`}
                >
                  <subItem.icon className="mr-3 h-4 w-4" />
                  <span>{subItem.label}</span>
                </Link>
              ) : (
                <button
                  key={subItem.id}
                  onClick={() => handleExerciseClick(subItem.id)}
                  className="flex items-center px-4 py-2 text-sm text-gray-300 rounded hover:bg-gray-700 hover:text-white w-full text-left"
                >
                  <subItem.icon className="mr-3 h-4 w-4" />
                  <span>{subItem.label}</span>
                </button>
              )
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Desktop sidebar navigation */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-10 left-0 border-r border-gray-700">
        <div className="flex-1 flex flex-col min-h-0 bg-gray-800">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <h1 className="text-white text-xl font-bold">Mental Edge Coach</h1>
            </div>
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive(item.path)
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <item.icon className="mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-300" />
                  {item.label}
                </Link>
              ))}
              
              {/* Tools Section */}
              {hamburgerItems.map(item => renderDesktopSection(item))}
              
              {/* Admin Dashboard Link - Only visible to admin */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive('/admin')
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  <Settings
                    className={`mr-3 flex-shrink-0 h-6 w-6 ${
                      isActive('/admin')
                        ? 'text-white'
                        : 'text-gray-400 group-hover:text-gray-300'
                    }`}
                  />
                  Admin Dashboard
                </Link>
              )}
              
              {/* Profile Link */}
              <Link
                to="/profile"
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                  isActive('/profile')
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <UserCircle className="mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-300" />
                Profile
              </Link>
              
              {/* Contact Us Button */}
              <button
                onClick={() => setActiveModal('contact-us')}
                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-300 hover:bg-gray-700 hover:text-white w-full text-left"
              >
                <MessageSquare className="mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-300" />
                Contact Us
              </button>
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="group flex items-center px-2 py-2 text-sm font-medium rounded-md text-red-400 hover:bg-gray-700 hover:text-red-300 w-full text-left"
              >
                <LogOutIcon className="mr-3 flex-shrink-0 h-6 w-6 text-red-400 group-hover:text-red-300" />
                Logout
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-800 z-10">
        <div className="flex justify-around">
          {mobilePrimaryItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center py-2 px-2 ${
                isActive(item.path) ? 'text-white' : 'text-gray-400'
              }`}
            >
              <item.icon size={22} />
              <span className="text-xs mt-1">{item.label}</span>
            </Link>
          ))}
          <button
            onClick={() => setIsHamburgerOpen(true)}
            className="flex items-center justify-center py-2 px-3 text-white bg-blue-600 rounded-t-lg relative -top-1 w-14 h-14 shadow-md"
            aria-label="Open menu"
          >
            <MenuIcon size={32} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Mobile hamburger menu */}
      {isHamburgerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsHamburgerOpen(false)}></div>
          <div className="fixed right-0 top-0 bottom-0 w-3/4 max-w-xs bg-gray-800 overflow-y-auto shadow-lg">
            <div className="flex justify-between items-center p-4 border-b border-gray-700 bg-blue-600">
              <h2 className="text-white text-lg font-bold">Menu</h2>
              <button onClick={() => setIsHamburgerOpen(false)} className="text-white p-2 rounded-full hover:bg-blue-700">
                <XIcon size={24} />
              </button>
            </div>
            <div className="p-4">
              {hamburgerItems.map((item) => {
                const isExpanded = expandedSections.mobile[item.label];
                
                return (
                  <div key={item.label} className="mb-4">
                    <button
                      onClick={() => toggleSection(item.label)}
                      className="w-full flex items-center justify-between p-2 rounded hover:bg-gray-700 text-white"
                    >
                      <div className="flex items-center">
                        <item.icon size={20} className="mr-3" />
                        {item.label}
                      </div>
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </button>
                    
                    {isExpanded && (
                      <div className="mt-2 ml-4 space-y-2">
                        {item.subItems.map((subItem) => (
                          subItem.path ? (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setIsHamburgerOpen(false)}
                              className={`w-full p-4 rounded hover:bg-gray-700 flex items-center ${
                                isActive(subItem.path) ? 'bg-blue-600' : ''
                              }`}
                            >
                              <subItem.icon size={24} className="mr-3" />
                              <span style={{ color: 'white' }}>{subItem.label}</span>
                            </Link>
                          ) : (
                            <button
                              key={subItem.id}
                              onClick={() => {
                                setActiveModal(subItem.id);
                                setIsHamburgerOpen(false);
                              }}
                              className="w-full p-4 rounded hover:bg-gray-700 flex items-center text-white"
                            >
                              <subItem.icon size={24} className="mr-3" />
                              <span style={{ color: 'white' }}>{subItem.label}</span>
                            </button>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Admin Dashboard Link - Only visible to admin */}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsHamburgerOpen(false)}
                  className={`w-full p-4 rounded hover:bg-gray-700 flex items-center ${
                    isActive('/admin') ? 'bg-blue-600' : ''
                  }`}
                >
                  <Settings size={24} className="mr-3" />
                  Admin Dashboard
                </Link>
              )}
              
              {/* Profile Link */}
              <Link
                to="/profile"
                onClick={() => setIsHamburgerOpen(false)}
                className={`w-full p-4 rounded hover:bg-gray-700 flex items-center text-white ${
                  isActive('/profile') ? 'bg-blue-600' : ''
                }`}
              >
                <UserCircle size={24} className="mr-3" />
                Profile
              </Link>
              
              {/* Contact Us Button */}
              <button
                onClick={() => {
                  setActiveModal('contact-us');
                  setIsHamburgerOpen(false);
                }}
                className="w-full p-4 rounded hover:bg-gray-700 flex items-center text-white"
              >
                <MessageSquare size={24} className="mr-3" />
                Contact Us
              </button>
              
              {/* Add to Home Screen Button - Only visible on mobile */}
              {isMobile && (
                <button
                  onClick={() => {
                    setActiveModal('home-screen-instructions');
                    setIsHamburgerOpen(false);
                  }}
                  className="w-full p-4 rounded hover:bg-gray-700 flex items-center text-white"
                >
                  <Smartphone size={24} className="mr-3" />
                  Add to Home Screen
                </button>
              )}
              
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-full p-4 rounded hover:bg-gray-700 flex items-center text-red-400"
              >
                <LogOutIcon size={24} className="mr-3" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      
      {activeModal === 'ask-your-coach' && (
        <Modal isOpen={true} onClose={() => setActiveModal(null)}>
          <AskYourCoach onComplete={() => setActiveModal(null)} />
        </Modal>
      )}
      
      {activeModal === 'contact-us' && (
        <Modal isOpen={true} onClose={() => setActiveModal(null)}>
          <ContactUs onClose={() => setActiveModal(null)} />
        </Modal>
      )}
      
      {activeModal === 'home-screen-instructions' && (
        <Modal isOpen={true} onClose={() => setActiveModal(null)}>
          <HomeScreenInstructions onClose={() => setActiveModal(null)} />
        </Modal>
      )}
    </>
  );
};

export default Navigation;